import { Box, Typography, Slider, Button } from "@mui/material";
import React from "react";
import { useLogger } from "../../../contexts/LoggerContext";

type GainLossPosition = {
  value: number;
  label: string;
};

// Function to calculate valid min/max values based on ball position
const calculateValidRange = (
  ballOn: number | null
): { min: number; max: number } => {
  if (ballOn === null) {
    // If ball position is unknown, use full range
    return { min: -99, max: 99 };
  }
  const minValue = ballOn > 0 ? -(50-ballOn +50) : ballOn + 1;
  const maxValue = ballOn > 0 ? ballOn-1 : (50 - Math.abs(ballOn))+49;

  return { min: minValue, max: maxValue };
};

const calculateNewSpot = (ballOn: number | null, gainLoss: number): number => {
  if (ballOn === null) {
    return 0;
  }

  const startInOwnTerritory = ballOn < 0;
  let newSpot: number = 0;

  if (startInOwnTerritory) {
    newSpot = ballOn + -gainLoss;
    if (newSpot <= -50) {
      newSpot = 50 - (Math.abs(newSpot) - 50);
    }
  } else {
    newSpot = ballOn - gainLoss;
    if (newSpot >= 50) {
      newSpot = -(50 - (newSpot - 50));
    }
  }
  return newSpot;
};


// Create array of positions from -99 to 99
const GAIN_LOSS_POSITIONS: GainLossPosition[] = Array.from(
  { length: 199 }, // -99 to 99 inclusive = 199 positions
  (_, i) => {
    const value = i - 99; // This gives us -99 to 99
    return {
      value,
      label: value.toString(),
    };
  }
);

export const GainLossSlider: React.FC<{
  value: number;
  onChange: (value: number) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const { gameSituation, touchdown, safety, setTouchdown, setSafety, setGameSituation } = useLogger();
  const { min, max } = calculateValidRange(gameSituation.yardline);
  const newSpot = calculateNewSpot(gameSituation.yardline, value);

  // Filter positions to only include valid range
  const validPositions = GAIN_LOSS_POSITIONS.filter(
    (pos) => pos.value >= min && pos.value <= max
  );

  // Find the current index in our filtered list
  const currentIndex = validPositions.findIndex((pos) => pos.value === value);

  // Create marks for every 10 yards and 0 within valid range
  const marks = validPositions.map((pos, index) => {
    if (pos.value === 0 || pos.value % 10 === 0) {
      return { value: index, label: pos.label };
    }
    return { value: index };
  });

  // Find the label for the current value
  const currentLabel = validPositions[currentIndex]?.label ?? value.toString();

  const zeroIndex = validPositions.findIndex((pos) => pos.value === 0);
  const highlightStart = Math.min(zeroIndex, currentIndex);
  const highlightEnd = Math.max(zeroIndex, currentIndex);

  const handleTouchdown = () => {
    setTouchdown(!touchdown);
    onChange(0);
    if (!touchdown) {
    setGameSituation({
      ...gameSituation,
      club_score: gameSituation.club_score + 6,
      });
    } else {
      setGameSituation({
        ...gameSituation,
        club_score: gameSituation.club_score - 6,
      });
    }
  };

  const handleSafety = () => {
    setSafety(!safety);
    onChange(0);
    if (!safety) {
    setGameSituation({
      ...gameSituation,
      opp_score: gameSituation.opp_score + 2,
    });
    } else {
      setGameSituation({
        ...gameSituation,
        opp_score: gameSituation.opp_score - 2,
      });
    }
  };

  return (
    <Box sx={{ width: "100%", px: 2, position: "relative" }}>
      {/* details */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        {/* Gain/Loss Label */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mr: 2,
          }}
        >
          <Typography id={`${label}-slider-label`} gutterBottom>
            Gain
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 0.5,
              fontWeight: "bold",
              color:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "text.primary",
            }}
          >
            {value > 0 ? `+${currentLabel}` : currentLabel} Yards
          </Typography>
        </Box>

        {/* Ball Position */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            ml: 2,
          }}
        >
          <Typography id={`${label}-slider-label`} gutterBottom>
            Ball Spotted
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 0.5,
              fontWeight: "bold",
              color:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "text.primary",
            }}
          >
            {newSpot} Yard Line
          </Typography>
        </Box>
      </Box>

      {/* Slider */}
      <Box sx={{ position: "relative", width: "100%" }}>
        <Box
          sx={{
            position: "absolute",
            height: 8,
            borderRadius: 4,
            top: "50%",
            left: `${(highlightStart / (validPositions.length - 1)) * 100}%`,
            width: `${
              ((highlightEnd - highlightStart) / (validPositions.length - 1)) *
              100
            }%`,
            backgroundColor:
              value > 0
                ? "success.main"
                : value < 0
                ? "error.main"
                : "primary.main",
            transform: "translateY(-50%)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
        <Slider
          value={currentIndex}
          onChange={(_event, newValue) => {
            const newIndex = newValue as number;
            onChange(validPositions[newIndex].value);
          }}
          aria-labelledby={`${label}-slider-label`}
          step={null}
          marks={marks}
          min={0}
          max={validPositions.length - 1}
          valueLabelDisplay="auto"
          valueLabelFormat={(index) => {
            const val = validPositions[index].value;
            return val > 0 ? `+${val}` : val.toString();
          }}
          sx={{
            "& .MuiSlider-markLabel": {
              fontSize: "0.75rem",
            },
            "& .MuiSlider-valueLabel": {
              fontSize: "0.875rem",
            },
            "& .MuiSlider-thumb": {
              backgroundColor:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "primary.main",
            },
            "& .MuiSlider-track": {
              backgroundColor:
                value > 0
                  ? "success.main"
                  : value < 0
                  ? "error.main"
                  : "primary.main",
            },
          }}
          track={false}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Button
          variant={safety ? "contained" : "outlined"}
          color={safety ? "error" : "primary"}
          sx={{ minWidth: 150, fontWeight: safety ? 'bold' : 'normal' }}
          onClick={handleSafety}
        >
          Safety
        </Button>
        <Button
          variant={touchdown ? "contained" : "outlined"}
          color={touchdown ? "success" : "primary"}
          sx={{ minWidth: 150, fontWeight: touchdown ? 'bold' : 'normal' }}
          onClick={handleTouchdown}
        >
          Touchdown
        </Button>
      </Box>
    </Box>
  );
};
